import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { BROKERS, SYMBOLS, SOURCE_COLORS, formatMoney, formatMoneyExact, EXIT_TYPE_BY_VALUE } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "../components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, FileText, Download, FilterX, Plus, X as XIcon, Camera, ChevronDown, ChevronUp } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ScreenshotLightbox from "../components/ScreenshotLightbox";
import MfeMaeCard from "../components/MfeMaeCard";
import DeltaSyncBar from "../components/DeltaSyncBar";

// The Side column must show POSITION DIRECTION (LONG/SHORT), never the
// raw opening-fill side (buy/sell). Journal docs store buy/sell for
// compatibility, but buy⟺long and sell⟺short for a closed round-trip.
function sideLabel(side) {
  const s = String(side || "").toLowerCase();
  if (s === "long" || s === "buy") return "LONG";
  if (s === "short" || s === "sell") return "SHORT";
  return s.toUpperCase();
}
function isLongSide(side) {
  const s = String(side || "").toLowerCase();
  return s === "long" || s === "buy";
}


export default function Trades() {
  const { currency, rate } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trades, setTrades] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [openExec, setOpenExec] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    symbol: searchParams.get("symbol") || "all",
    broker: searchParams.get("broker") || "all",
    broker_type: searchParams.get("broker_type") || "all",
  });
  const [emotionFilter, setEmotionFilter] = useState(searchParams.get("emotion") || "");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "");
  // Iter 113 — data-pipe filter (csv | manual | delta_api). Set by the
  // Delta Connect "Open my synced trades" CTA so a fresh sync lands the
  // user directly on their auto-synced rows, not on an empty upload form.
  const [dataSourceFilter, setDataSourceFilter] = useState(searchParams.get("data_source") || "");
  const [page, setPage] = useState(0); // 0-indexed
  const PAGE_SIZE = 20;

  // Screenshot lightbox state
  const [lightbox, setLightbox] = useState({ open: false, src: null, caption: null, loading: false });
  // iter126 — per-row expand state for the MFE/MAE detail card.
  // Uses a Set so O(1) add/remove and cheap toggle. Cleared on refresh.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const openScreenshot = async (trade) => {
    setLightbox({
      open: true, src: null, loading: true,
      caption: { symbol: trade.symbol, date: trade.date, side: trade.side },
    });
    try {
      const { data } = await api.get(`/trades/${trade.id}`);
      setLightbox((l) => ({ ...l, src: data?.screenshot || null, loading: false }));
    } catch (e) {
      toast.error("Couldn't load screenshot");
      setLightbox({ open: false, src: null, caption: null, loading: false });
    }
  };
  const closeLightbox = () => setLightbox({ open: false, src: null, caption: null, loading: false });

  const load = async (overridePage, overrideFilters, overrideEmotion, overrideSource) => {
    setLoading(true);
    try {
      const currentPage = overridePage != null ? overridePage : page;
      const f = overrideFilters || filters;
      const e = overrideEmotion !== undefined ? overrideEmotion : emotionFilter;
      const s = overrideSource !== undefined ? overrideSource : sourceFilter;
      const params = { limit: PAGE_SIZE, offset: currentPage * PAGE_SIZE };
      if (f.start_date) params.start_date = f.start_date;
      if (f.end_date) params.end_date = f.end_date;
      if (f.symbol && f.symbol !== "all") params.symbol = f.symbol;
      if (f.broker && f.broker !== "all") params.broker = f.broker;
      if (f.broker_type && f.broker_type !== "all") params.broker_type = f.broker_type;
      if (e) params.emotion = e;
      if (s) params.source = s;
      if (dataSourceFilter) params.data_source = dataSourceFilter;
      const { data } = await api.get("/trades", { params });
      const items = Array.isArray(data) ? data : data.items || [];
      const tot = Array.isArray(data) ? items.length : data.total ?? items.length;
      setTrades(items);
      setTotal(tot);
    } finally {
      setLoading(false);
    }
  };

  // Sync filters from URL on first mount (Pattern banner & Heatmap deep-links)
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

  // Phase 3 (FIX 3) — currently-open positions (from API sync). Shown in a
  // dedicated section; excluded from the closed-trade table + stats.
  const loadOpenPositions = () => {
    api.get("/open-positions")
      .then(({ data }) => setOpenPositions(data?.open_positions || []))
      .catch(() => setOpenPositions([]));
  };
  useEffect(() => { loadOpenPositions(); }, []);

  // Phase 4 — after a manual "Sync now", refresh both the closed-trade
  // table and the open-positions banner so new fills surface immediately.
  const onDeltaSynced = () => { load(); loadOpenPositions(); };

  // Active deep-link chips (from URL or non-default state)
  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.start_date || filters.end_date) {
      const lbl = filters.start_date && filters.end_date && filters.start_date === filters.end_date
        ? filters.start_date
        : `${filters.start_date || "…"} → ${filters.end_date || "…"}`;
      chips.push({ key: "date", label: `Date: ${lbl}` });
    }
    if (filters.symbol && filters.symbol !== "all") chips.push({ key: "symbol", label: `Symbol: ${filters.symbol}` });
    if (filters.broker && filters.broker !== "all") chips.push({ key: "broker", label: `Broker: ${filters.broker}` });
    if (filters.broker_type && filters.broker_type !== "all") chips.push({ key: "broker_type", label: `Type: ${filters.broker_type.replace("_RETAIL", "").replace("_FIRM", " FIRM")}` });
    if (emotionFilter) chips.push({ key: "emotion", label: `Emotion: ${emotionFilter}` });
    if (sourceFilter) chips.push({ key: "source", label: `Source: ${sourceFilter}` });
    if (dataSourceFilter) {
      const dsLabel = {
        delta_api: "Auto-synced from Delta",
        csv: "CSV imports only",
        manual: "Manual entries only",
      }[dataSourceFilter] || dataSourceFilter;
      chips.push({ key: "data_source", label: dsLabel });
    }
    return chips;
  }, [filters, emotionFilter, sourceFilter, dataSourceFilter]);

  const clearAll = () => {
    const next = { start_date: "", end_date: "", symbol: "all", broker: "all", broker_type: "all" };
    setFilters(next);
    setEmotionFilter("");
    setSourceFilter("");
    setDataSourceFilter("");
    setSearchParams({});
    setPage(0);
    load(0, next, "", "");
  };

  const clearChip = (key) => {
    let next = { ...filters };
    let nextEmotion = emotionFilter;
    let nextSource = sourceFilter;
    let nextDataSource = dataSourceFilter;
    if (key === "date") { next.start_date = ""; next.end_date = ""; }
    if (key === "symbol") next.symbol = "all";
    if (key === "broker") next.broker = "all";
    if (key === "broker_type") next.broker_type = "all";
    if (key === "emotion") nextEmotion = "";
    if (key === "source") nextSource = "";
    if (key === "data_source") nextDataSource = "";
    setFilters(next);
    setEmotionFilter(nextEmotion);
    setSourceFilter(nextSource);
    setDataSourceFilter(nextDataSource);
    setPage(0);
    // Sync URL
    const sp = new URLSearchParams();
    if (next.start_date) sp.set("start_date", next.start_date);
    if (next.end_date) sp.set("end_date", next.end_date);
    if (next.symbol !== "all") sp.set("symbol", next.symbol);
    if (next.broker !== "all") sp.set("broker", next.broker);
    if (next.broker_type !== "all") sp.set("broker_type", next.broker_type);
    if (nextEmotion) sp.set("emotion", nextEmotion);
    if (nextSource) sp.set("source", nextSource);
    if (nextDataSource) sp.set("data_source", nextDataSource);
    setSearchParams(sp);
    load(0, next, nextEmotion, nextSource);
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/trades/${id}`);
      toast.success("Trade deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete trade");
    }
  };

  const onResetFilters = () => {
    clearAll();
  };

  const onApplyFilters = () => {
    setPage(0);
    // Update URL to reflect manual filter
    const sp = new URLSearchParams();
    if (filters.start_date) sp.set("start_date", filters.start_date);
    if (filters.end_date) sp.set("end_date", filters.end_date);
    if (filters.symbol !== "all") sp.set("symbol", filters.symbol);
    if (filters.broker !== "all") sp.set("broker", filters.broker);
    if (filters.broker_type !== "all") sp.set("broker_type", filters.broker_type);
    if (emotionFilter) sp.set("emotion", emotionFilter);
    if (sourceFilter) sp.set("source", sourceFilter);
    setSearchParams(sp);
    load(0);
  };

  const goToPage = (next) => {
    setPage(next);
    load(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const displayedNetPnl = (t) => {
    if (t.pnl_unverified) return null;   // Fix 1a — no computed P&L
    const net = t.net_pnl != null ? t.net_pnl : t.pnl;
    if (currency === t.currency) return net;
    if (currency === "INR" && t.currency === "USD") return net * rate;
    if (currency === "USD" && t.currency === "INR") return rate > 0 ? net / rate : 0;
    return net;
  };

  const displayedGrossPnl = (t) => {
    if (currency === t.currency) return t.pnl;
    if (currency === "INR" && t.currency === "USD") return t.pnl * rate;
    if (currency === "USD" && t.currency === "INR") return rate > 0 ? t.pnl / rate : 0;
    return t.pnl;
  };

  const totalDisplayed = useMemo(
    () => trades.reduce((acc, t) => acc + (displayedNetPnl(t) || 0), 0),
    // eslint-disable-next-line
    [trades, currency, rate]
  );

  const exportCSV = async () => {
    try {
      const res = await api.get("/trades/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `braintrades_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(16);
      doc.text("BrainTRADES — Trading Journal", 14, 16);
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(`Generated: ${new Date().toLocaleString()}  ·  Display Currency: ${currency}  ·  USD→INR: ${rate}`, 14, 22);

      autoTable(doc, {
        startY: 28,
        head: [["Date", "Broker", "Type", "Symbol", "Side", "Acct", "Entry", "Exit", "Qty",
                "Gross P&L", "Trading Fee", `Net (${currency})`, "R", "Your Take",
                "Exit", "Emotion", "Source"]],
        body: trades.map((t) => {
          const trading = t.trading_fees ?? 0;
          const exitLabel = (t.exit_type && t.exit_type !== "MANUAL")
            ? t.exit_type.replace("_", " ").replace("HIT", "Hit").replace("EARLY", "Early")
            : "—";
          return [
            t.date, t.broker,
            (t.broker_type || "EQUITY").replace("_RETAIL", "").replace("_FIRM", "").toUpperCase().slice(0, 6),
            t.symbol, sideLabel(t.side),
            t.account_type ? t.account_type.replace(" / Zero", "/0").slice(0, 8) : "—",
            t.entry_price, t.exit_price, t.quantity,
            t.pnl_unverified ? "Unverified" : formatMoney(t.pnl, t.currency),
            trading ? formatMoney(trading, t.currency) + (t.trading_fees_auto_estimated ? " (auto)" : "") : "—",
            t.pnl_unverified ? "Unverified" : formatMoney(displayedNetPnl(t), currency),
            t.r_multiple == null ? "—" : `1:${t.r_multiple.toFixed(1)}`,
            t.your_take != null ? formatMoney(t.your_take, t.currency) : "—",
            exitLabel,
            t.emotion, t.source || "Own Analysis",
          ];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [0, 229, 160], textColor: 0, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      doc.save(`braintrades_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const inputCls = "bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-10 rounded-sm";
  const selectCls = "bg-transparent border-[var(--bt-border)] focus:border-[var(--bt-accent)] focus:ring-1 focus:ring-[var(--bt-accent)] h-10 rounded-sm";

  return (
    <div className="space-y-6" data-testid="trades-page">
      <DeltaSyncBar onSynced={onDeltaSynced} />
      {openPositions.length > 0 && (
        <div data-testid="open-positions-section" className="rounded-lg border border-[var(--bt-warn)]/40 bg-[var(--bt-warn)]/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-heading text-lg" style={{ color: "var(--bt-text)" }}>Open Positions</h2>
            <span className="rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide" style={{ background: "color-mix(in srgb, var(--bt-warn) 20%, transparent)", color: "var(--bt-warn)" }}>{openPositions.length} open</span>
            <span className="text-xs text-[var(--bt-text-muted)]">Excluded from stats · realized P&amp;L already banked · unrealized not included</span>
          </div>
          <div className="space-y-2">
            {openPositions.map((p) => {
              const isLong = String(p.side || "").toLowerCase() === "long";
              const ekey = p.trade_id || p.symbol;
              const execOpen = !!openExec[ekey];
              const nExec = (p.executions || []).length;
              return (
                <div key={ekey} data-testid={`open-position-${p.symbol}`} className="rounded-md border border-[var(--bt-border)] bg-[var(--bt-surface)]/60 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-mono font-semibold" style={{ color: "var(--bt-text)" }}>{p.option_label || p.symbol}</span>
                    <span data-testid={`open-side-${p.symbol}`} className="rounded px-1 py-[1px] text-[9px] font-bold uppercase" style={{ background: isLong ? "color-mix(in srgb, var(--bt-win) 15%, transparent)" : "color-mix(in srgb, var(--bt-loss) 15%, transparent)", color: isLong ? "var(--bt-win)" : "var(--bt-loss)" }}>{isLong ? "LONG" : "SHORT"}</span>
                    <span className="rounded px-1 py-[1px] text-[9px] font-bold uppercase" style={{ background: "color-mix(in srgb, var(--bt-warn) 20%, transparent)", color: "var(--bt-warn)" }}>OPEN</span>
                    <span className="font-mono text-[10px] text-[var(--bt-text-muted)]" data-testid={`open-trade-id-${p.symbol}`}>#{p.trade_id}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-[2px] text-xs text-[var(--bt-text)]">
                    <span>Open qty: <span className="font-mono font-semibold">{Number(p.open_qty)}</span></span>
                    <span>Entry: <span className="font-mono">{p.entry_price?.toLocaleString?.() ?? p.entry_price}</span></span>
                    <span>Carried fee: <span className="font-mono font-semibold" data-testid={`open-carried-fee-${p.symbol}`}>{formatMoney(p.carried_fee || 0, currency)}</span></span>
                    <span>Realized so far: <span className="font-mono font-semibold" style={{ color: (p.realized_so_far || 0) >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}>{p.pnl_unverified ? "Unverified" : formatMoney(p.realized_so_far || 0, currency)}</span></span>
                  </div>
                  {nExec > 0 && (
                    <button data-testid={`open-exec-toggle-${p.symbol}`} className="mt-1 text-[11px] underline text-[var(--bt-text-muted)]" onClick={() => setOpenExec((m) => ({ ...m, [ekey]: !m[ekey] }))}>
                      {execOpen ? "▾ Hide" : "▸ Show"} {nExec} execution{nExec === 1 ? "" : "s"}
                    </button>
                  )}
                  {execOpen && (
                    <table className="w-full text-[11px] mt-1" data-testid={`open-exec-table-${p.symbol}`}>
                      <thead><tr className="text-left text-[var(--bt-text-muted)]"><th className="px-2 py-[2px]">Role</th><th className="px-2 py-[2px]">Side</th><th className="px-2 py-[2px] text-right">Qty</th><th className="px-2 py-[2px] text-right">Price</th><th className="px-2 py-[2px] text-right">Fee</th><th className="px-2 py-[2px]">Time</th></tr></thead>
                      <tbody>
                        {(p.executions || []).map((e, ei) => (
                          <tr key={ei} data-testid={`open-exec-row-${p.symbol}-${ei}`} className="border-t border-[var(--bt-border)]/20">
                            <td className="px-2 py-[2px] uppercase">{e.role}</td>
                            <td className="px-2 py-[2px] uppercase">{e.side}</td>
                            <td className="px-2 py-[2px] text-right font-mono">{Number(e.qty)}</td>
                            <td className="px-2 py-[2px] text-right font-mono">{e.price?.toLocaleString?.() ?? e.price}</td>
                            <td className="px-2 py-[2px] text-right font-mono">{e.fee}</td>
                            <td className="px-2 py-[2px] font-mono text-[9px]">{(e.time || "").replace("T", " ").slice(0, 19)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="bt-label">Journal</p>
          <h1 className="font-heading text-4xl">Trades</h1>
          <p className="text-sm text-[var(--bt-text-muted)] mt-1">
            {total} entries · Showing {trades.length} on this page · Net P&L (this page):{" "}
            <span className={totalDisplayed >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}>
              {formatMoney(totalDisplayed, currency)}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          <Button onClick={exportPDF} variant="outline" data-testid="export-pdf-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm">
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Link to="/trades/new" data-testid="new-trade-btn"
            className="inline-flex items-center px-4 py-2 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm transition-colors">
            <Plus className="w-4 h-4 mr-1" /> New
          </Link>
        </div>
      </div>

      {/* Active filter chips (from URL deep-links) */}
      {activeChips.length > 0 && (
        <div
          data-testid="active-filter-chips"
          className="flex flex-wrap items-center gap-2 -mt-2"
        >
          <span className="bt-label">Active filters</span>
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              data-testid={`active-chip-${chip.key}`}
              onClick={() => clearChip(chip.key)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border border-[var(--bt-accent)]/40 bg-[var(--bt-accent)]/10 text-[var(--bt-accent)] text-xs font-semibold hover:bg-[var(--bt-accent)]/20 transition-colors duration-150"
            >
              {chip.label}
              <XIcon className="w-3 h-3" />
            </button>
          ))}
          <button
            data-testid="clear-all-chips"
            onClick={clearAll}
            className="text-xs text-[var(--bt-text-muted)] hover:text-white uppercase tracking-wider"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Iter 18 — Broker-type quick filter chips. Server-side filter via
          /api/trades?broker_type=... — works across paginated results. */}
      <div
        data-testid="broker-type-filter-chips"
        className="flex flex-wrap items-center gap-2"
      >
        {[
          { v: "all",        label: "All",       color: "var(--bt-accent)" },
          { v: "CRYPTO",     label: "Crypto",    color: "#fbbf24" },
          { v: "CFD_RETAIL", label: "CFD",       color: "#60a5fa" },
          { v: "PROP_FIRM",  label: "Prop Firm", color: "#a78bfa" },
          { v: "EQUITY",     label: "Equity",    color: "#94a3b8" },
        ].map((c) => {
          const active = filters.broker_type === c.v;
          return (
            <button
              key={c.v}
              type="button"
              data-testid={`broker-type-chip-${c.v}`}
              aria-pressed={active}
              onClick={() => {
                const next = { ...filters, broker_type: c.v };
                setFilters(next);
                setPage(0);
                const sp = new URLSearchParams();
                if (next.start_date) sp.set("start_date", next.start_date);
                if (next.end_date) sp.set("end_date", next.end_date);
                if (next.symbol !== "all") sp.set("symbol", next.symbol);
                if (next.broker !== "all") sp.set("broker", next.broker);
                if (next.broker_type !== "all") sp.set("broker_type", next.broker_type);
                if (emotionFilter) sp.set("emotion", emotionFilter);
                if (sourceFilter) sp.set("source", sourceFilter);
                setSearchParams(sp);
                load(0, next);
              }}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide border transition-all duration-150"
              style={active ? {
                background: `${c.color}1f`,
                borderColor: c.color,
                color: c.color,
                boxShadow: `0 0 0 1px ${c.color}`,
              } : {
                background: "var(--bt-surface-hover)",
                borderColor: "var(--bt-border)",
                color: "var(--bt-text-muted)",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div
        data-testid="trades-filters"
        className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end"
      >
        <div className="space-y-1">
          <Label className="bt-label">From</Label>
          <Input type="date" value={filters.start_date}
            onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
            data-testid="filter-start-date" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="bt-label">To</Label>
          <Input type="date" value={filters.end_date}
            onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
            data-testid="filter-end-date" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="bt-label">Symbol</Label>
          <Select value={filters.symbol} onValueChange={(v) => setFilters((f) => ({ ...f, symbol: v }))}>
            <SelectTrigger data-testid="filter-symbol" className={selectCls}><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All symbols</SelectItem>
              {SYMBOLS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="bt-label">Broker</Label>
          <Select value={filters.broker} onValueChange={(v) => setFilters((f) => ({ ...f, broker: v }))}>
            <SelectTrigger data-testid="filter-broker" className={selectCls}><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All brokers</SelectItem>
              {BROKERS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={onApplyFilters} data-testid="apply-filters-btn"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-10 flex-1">
            Apply
          </Button>
          <Button onClick={onResetFilters} variant="outline" data-testid="reset-filters-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm h-10">
            <FilterX className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* iter123 — Mobile card view (<640px). Stacked layout that avoids
          horizontal scroll: header line = date + symbol + side + fill-type
          chip, middle = entry→exit + qty, bottom = prominent net P&L +
          broker + emotion + actions. Loading / empty states mirrored. */}
      <div className="sm:hidden space-y-2" data-testid="trades-mobile-list">
        {loading && (
          <div className="bt-card border border-[var(--bt-border)] rounded-sm p-8 text-center text-[var(--bt-text-muted)] text-sm">
            Loading…
          </div>
        )}
        {!loading && trades.length === 0 && (
          <div className="bt-card border border-[var(--bt-border)] rounded-sm p-8 text-center text-[var(--bt-text-muted)] text-sm">
            No trades yet.{" "}
            <Link to="/trades/new" className="text-[var(--bt-accent)] hover:underline">
              Log your first trade →
            </Link>
          </div>
        )}
        {!loading && trades.map((t) => {
          const netDisp = displayedNetPnl(t);
          const netPos = netDisp >= 0;
          const src = t.source || "Own Analysis";
          const srcColor = SOURCE_COLORS[src] || "#888";
          const ft = String(t.fill_type || "normal").toLowerCase();
          const ftChip = ft === "normal" ? null : {
            liquidation: { label: "LIQ", fg: "var(--bt-danger)" },
            adl:         { label: "ADL", fg: "#ff9500" },
            settlement:  { label: "SETTLE", fg: "var(--bt-text-muted)" },
            otc:         { label: "OTC", fg: "var(--bt-text-muted)" },
          }[ft];
          return (
            <article
              key={t.id}
              data-testid={`trade-card-${t.id}`}
              className="bt-card border rounded-sm p-3 bg-[var(--bt-surface)]"
              style={{ borderColor: "var(--bt-border)" }}
            >
              {/* Header line: date · symbol · side (+ fill-type chip) */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="font-mono text-[11px] text-[var(--bt-text-muted)] whitespace-nowrap">
                  {t.date}
                </span>
                <span className="font-semibold text-base">{t.symbol}</span>
                <span
                  data-testid={`trade-side-${t.id}`}
                  className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold border ${
                    isLongSide(t.side)
                      ? "border-[var(--bt-accent)]/50 text-[var(--bt-accent)]"
                      : "border-[var(--bt-danger)]/50 text-[var(--bt-danger)]"
                  }`}
                >
                  {sideLabel(t.side)}
                </span>
                {ftChip && (
                  <span
                    data-testid={`trade-card-fill-chip-${t.id}`}
                    className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold border"
                    style={{ color: ftChip.fg, borderColor: `${ftChip.fg}66` }}
                  >
                    {ftChip.label}
                  </span>
                )}
                {t.data_source === "delta_api" && (
                  <span
                    className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold uppercase tracking-wider"
                    style={{
                      background: "color-mix(in srgb, var(--bt-accent) 16%, var(--bt-surface))",
                      color: "var(--bt-accent)",
                      border: "1px solid color-mix(in srgb, var(--bt-accent) 45%, var(--bt-border))",
                    }}
                    title="Auto-synced from Delta"
                  >
                    SYNCED
                  </span>
                )}
              </div>

              {/* Middle line: entry → exit · qty */}
              <div className="text-xs text-[var(--bt-text-muted)] font-mono mb-3">
                <span data-testid={`trade-card-entry-${t.id}`}>{t.entry_price}</span>
                <span className="mx-1.5" aria-hidden="true">→</span>
                <span data-testid={`trade-card-exit-${t.id}`}>{t.exit_price}</span>
                <span className="mx-2 text-[var(--bt-border)]">·</span>
                <span data-testid={`trade-card-qty-${t.id}`}>Qty {t.quantity}</span>
                {t.r_multiple != null && (
                  <>
                    <span className="mx-2 text-[var(--bt-border)]">·</span>
                    <span className={t.r_multiple >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}>
                      1:{t.r_multiple.toFixed(1)}R
                    </span>
                  </>
                )}
              </div>

              {/* Bottom row: prominent net P&L on the left, actions on the right */}
              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">
                    Net P&amp;L
                  </div>
                  <div
                    data-testid={`trade-card-pnl-${t.id}`}
                    className={`font-mono font-bold leading-tight ${
                      t.pnl_unverified
                        ? "text-[11px] text-[#d9a406]"
                        : `text-xl ${netPos ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}`
                    }`}
                    title={t.pnl_unverified ? (t.pnl_unverified_reason || "Contract specification unverified — P&L not calculated") : undefined}
                  >
                    {t.pnl_unverified ? "Unverified — P&L not calculated" : formatMoney(netDisp, currency)}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[10px] text-[var(--bt-text-muted)] truncate max-w-[10rem]">
                      {t.broker}
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded-sm text-[10px] border"
                      style={{ borderColor: `${srcColor}66`, color: srcColor, background: `${srcColor}10` }}
                    >
                      {src}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {t.has_screenshot && (
                    <button
                      type="button"
                      data-testid={`trade-card-screenshot-${t.id}`}
                      onClick={() => openScreenshot(t)}
                      aria-label="View chart screenshot"
                      className="w-11 h-11 flex items-center justify-center border rounded-sm"
                      style={{
                        borderColor: "rgba(0, 255, 136, 0.3)",
                        color: "#00ff88",
                        background: "rgba(0, 255, 136, 0.06)",
                      }}
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  )}
                  {/* iter126 — details toggle. Reveals the MFE/MAE
                      card inline. Icon flips to Up when expanded. */}
                  <button
                    type="button"
                    data-testid={`trade-card-toggle-detail-${t.id}`}
                    aria-label={expanded.has(t.id) ? "Hide trade detail" : "Show trade detail"}
                    aria-expanded={expanded.has(t.id)}
                    onClick={() => toggleExpanded(t.id)}
                    className="w-11 h-11 flex items-center justify-center border border-[var(--bt-border)] hover:border-[var(--bt-accent)] rounded-sm"
                  >
                    {expanded.has(t.id)
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <Link
                    to={`/trades/${t.id}/edit`}
                    data-testid={`trade-card-edit-${t.id}`}
                    aria-label="Edit trade"
                    className="w-11 h-11 flex items-center justify-center border border-[var(--bt-border)] hover:border-[var(--bt-accent)] rounded-sm"
                  >
                    <Pencil className="w-4 h-4" />
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`trade-card-delete-${t.id}`}
                        aria-label="Delete trade"
                        className="w-11 h-11 flex items-center justify-center border border-[var(--bt-border)] hover:border-[var(--bt-danger)] hover:text-[var(--bt-danger)] rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[var(--bt-surface)] border-[var(--bt-border)]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Delete this trade?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t.symbol} · {t.date} · {formatMoney(t.pnl, t.currency)} — this cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[var(--bt-border)] hover:bg-[var(--bt-surface-hover)]">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`trade-card-confirm-delete-${t.id}`}
                          onClick={() => onDelete(t.id)}
                          className="bg-[var(--bt-danger)] hover:bg-[var(--bt-danger-hover)] text-white"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* iter126 — expanded detail: MFE/MAE card, mounted only
                  when the user opens this row. Mount unmounts on
                  collapse to keep memory tidy; the backend caches
                  enrichment to the trade doc so re-open is instant. */}
              {expanded.has(t.id) && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bt-border)" }}>
                  {t.data_source === "delta_api" && (t.trading_fees ?? 0) > 0 && (
                    <p
                      data-testid={`fee-truncation-note-mobile-${t.id}`}
                      className="text-[11px] text-[var(--bt-text-muted)] mb-3"
                    >
                      Fees shown rounded to 2 decimals. Exact commission:{" "}
                      <span className="font-mono text-[var(--bt-text)]">
                        {formatMoneyExact(t.trading_fees_exact ?? t.trading_fees, t.currency)}
                      </span>
                      . Delta's Fills tab truncates fees, so it may differ by $0.01.
                    </p>
                  )}
                  <MfeMaeCard tradeId={t.id} testid={`mfe-mae-mobile-${t.id}`} />
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/* Table — desktop only (≥640px). Below that, the mobile card view
          above takes over so users don't need to horizontal-scroll. */}
      <div className="hidden sm:block bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm overflow-x-auto">
        <Table data-testid="trades-table" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[var(--bt-border)]">
              <TableHead className="bt-label">Date</TableHead>
              <TableHead className="bt-label">Trade ID</TableHead>
              <TableHead className="bt-label">Symbol</TableHead>
              <TableHead className="bt-label">Broker</TableHead>
              <TableHead className="bt-label">Side</TableHead>
              <TableHead className="bt-label text-right">Entry</TableHead>
              <TableHead className="bt-label text-right">Exit</TableHead>
              <TableHead className="bt-label text-right">Qty</TableHead>
              <TableHead className="bt-label text-right">Net P&L</TableHead>
              <TableHead className="bt-label text-right">R:R</TableHead>
              <TableHead className="bt-label">Emotion</TableHead>
              <TableHead className="bt-label text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={13} className="text-center text-[var(--bt-text-muted)] py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && trades.length === 0 && (
              <TableRow><TableCell colSpan={13} className="text-center text-[var(--bt-text-muted)] py-12">
                No trades yet. <Link to="/trades/new" className="text-[var(--bt-accent)] hover:underline">Log your first trade →</Link>
              </TableCell></TableRow>
            )}
            {!loading && trades.map((t) => {
              const netDisp = displayedNetPnl(t);
              const grossDisp = displayedGrossPnl(t);
              const trading = t.trading_fees ?? 0;
              const funding = t.funding_fees ?? 0;
              // iter120 — Delta perpetual funding (signed: + = received, − = paid).
              // Rendered as a distinct line under trading commission.
              const deltaFunding = t.delta_funding ?? 0;
              const hasDeltaFunding = t.funding_ingested && Math.abs(deltaFunding) >= 0.0001;
              const hasFees = trading + funding > 0 || hasDeltaFunding;
              return (
                <React.Fragment key={t.id}>
                <TableRow data-testid={`trade-row-${t.id}`} className="border-[var(--bt-border)] hover:bg-[var(--bt-surface-hover)]">
                  <TableCell className="font-mono text-xs whitespace-nowrap">{t.date}</TableCell>
                  <TableCell className="font-mono text-[10px] text-[var(--bt-text-muted)] whitespace-nowrap" data-testid={`trade-id-${t.id}`} title={t.trade_id || t.id}>{t.trade_id || t.id}</TableCell>
                  <TableCell className="font-semibold">
                    {t.symbol}
                    {t.order_type && (
                      <span className="ml-1.5 text-[10px] font-normal text-[var(--bt-text-muted)] uppercase">
                        {t.order_type}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-[var(--bt-text-muted)]">
                    <div className="flex items-center gap-1.5">
                      <span>{t.broker}</span>
                      {t.data_source === "delta_api" && (
                        <span
                          data-testid={`auto-synced-badge-${t.id}`}
                          title="Auto-synced from Delta Exchange"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider"
                          style={{
                            background: "color-mix(in srgb, var(--bt-accent) 16%, var(--bt-surface))",
                            color: "var(--bt-accent)",
                            border: "1px solid color-mix(in srgb, var(--bt-accent) 45%, var(--bt-border))",
                          }}
                        >
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 Z" />
                          </svg>
                          Auto-sync
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span data-testid={`trade-side-${t.id}`} className={`px-2 py-0.5 rounded-sm text-xs font-bold border ${isLongSide(t.side)
                        ? "border-[var(--bt-accent)]/50 text-[var(--bt-accent)]"
                        : "border-[var(--bt-danger)]/50 text-[var(--bt-danger)]"}`}>
                        {sideLabel(t.side)}
                      </span>
                      {(() => {
                        const et = EXIT_TYPE_BY_VALUE[t.exit_type || "MANUAL"];
                        if (!et) return null;
                        return (
                          <span
                            data-testid={`exit-badge-${t.id}`}
                            title={`Exit: ${et.label}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-semibold tracking-[0.05em] border"
                            style={{
                              background: `${et.color}1a`,
                              borderColor: `${et.color}66`,
                              color: et.color,
                            }}
                          >
                            <span className="mr-0.5">{et.emoji}</span>
                            {et.short}
                          </span>
                        );
                      })()}
                      {/* iter121 — Delta fill-type chip. `normal` shows nothing
                          (clean default). Severity ordering: LIQ > ADL >
                          SETTLE > OTC. Uses inline color styles for
                          mobile-legible contrast on both light/dark surfaces. */}
                      {(() => {
                        const ft = String(t.fill_type || "normal").toLowerCase();
                        if (ft === "normal") return null;
                        const chip = {
                          liquidation: {
                            label: "LIQ",
                            fg: "var(--bt-danger)",
                            bg: "color-mix(in srgb, var(--bt-danger) 18%, var(--bt-surface))",
                            border: "color-mix(in srgb, var(--bt-danger) 55%, var(--bt-border))",
                            title: "Forced liquidation — Delta closed this position when your margin fell below the maintenance requirement. Excluded from behavioural stats since it wasn't a discretionary exit.",
                          },
                          adl: {
                            label: "ADL",
                            fg: "#ff9500",
                            bg: "color-mix(in srgb, #ff9500 18%, var(--bt-surface))",
                            border: "color-mix(in srgb, #ff9500 55%, var(--bt-border))",
                            title: "Auto-Deleveraging — Delta closed this position to socialise a counterparty's losses across profitable traders. Not your decision and excluded from behavioural stats.",
                          },
                          settlement: {
                            label: "SETTLE",
                            fg: "var(--bt-text-muted)",
                            bg: "color-mix(in srgb, var(--bt-text-muted) 15%, var(--bt-surface))",
                            border: "color-mix(in srgb, var(--bt-text-muted) 45%, var(--bt-border))",
                            title: "Contract settlement — the position closed at Delta's mark-to-market settlement price when the contract expired.",
                          },
                          otc: {
                            label: "OTC",
                            fg: "var(--bt-text-muted)",
                            bg: "color-mix(in srgb, var(--bt-text-muted) 15%, var(--bt-surface))",
                            border: "color-mix(in srgb, var(--bt-text-muted) 45%, var(--bt-border))",
                            title: "Over-the-counter block trade — filled off the public order book at a negotiated price.",
                          },
                        }[ft];
                        if (!chip) return null;
                        return (
                          <span
                            data-testid={`fill-type-chip-${t.id}`}
                            title={chip.title}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.08em] border whitespace-nowrap"
                            style={{
                              color: chip.fg,
                              background: chip.bg,
                              borderColor: chip.border,
                            }}
                          >
                            {chip.label}
                          </span>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right bt-num">{t.entry_price}</TableCell>
                  <TableCell className="text-right bt-num">{t.exit_price}</TableCell>
                  <TableCell className="text-right bt-num">{t.quantity}</TableCell>
                  <TableCell
                    data-testid={`trade-pnl-${t.id}`}
                    className={`text-right bt-num font-bold ${t.pnl_unverified ? "text-[#d9a406]" : (netDisp >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]")}`}
                  >
                    {t.pnl_unverified ? (
                      <div
                        data-testid={`trade-pnl-unverified-${t.id}`}
                        title={t.pnl_unverified_reason || "Contract specification unverified — P&L not calculated"}
                        className="text-[11px] font-semibold leading-tight whitespace-normal text-right"
                      >
                        Unverified — P&amp;L not calculated
                      </div>
                    ) : (
                      <div>{formatMoney(netDisp, currency)}</div>
                    )}
                    {!t.pnl_unverified && hasFees && (
                      <div
                        data-testid={`trade-gross-${t.id}`}
                        className="text-[10px] font-normal text-[var(--bt-text-muted)] mt-0.5 whitespace-nowrap"
                      >
                        Gross {formatMoney(grossDisp, currency)}
                        {trading > 0 && (
                          <>
                            {" · "}
                            <span
                              data-testid={`trade-trading-fee-${t.id}`}
                              title={`Exact commission Delta deducted: ${formatMoneyExact(t.trading_fees_exact ?? trading, t.currency)}${t.data_source === "delta_api" ? " — Delta's Fills tab truncates fees, so it may differ by $0.01." : ""}`}
                            >
                              trading -{formatMoney(trading, t.currency)}
                            </span>
                            {t.trading_fees_auto_estimated && (
                              <span className="ml-1 text-[var(--bt-accent)]/80">auto</span>
                            )}
                          </>
                        )}
                        {funding > 0 && <>{" · "}funding -{formatMoney(funding, t.currency)}</>}
                        {hasDeltaFunding && (
                          <>
                            {" · "}
                            <span
                              data-testid={`trade-delta-funding-${t.id}`}
                              title={deltaFunding >= 0
                                ? `Delta perpetual funding you RECEIVED while holding this position (across ${t.funding_events_count || 0} funding cycle${(t.funding_events_count || 0) === 1 ? "" : "s"})`
                                : `Delta perpetual funding you PAID while holding this position (across ${t.funding_events_count || 0} funding cycle${(t.funding_events_count || 0) === 1 ? "" : "s"})`}
                              className={deltaFunding >= 0
                                ? "text-[var(--bt-accent)]"
                                : "text-[var(--bt-danger)]"}
                            >
                              funding {deltaFunding >= 0 ? "+" : "-"}{formatMoney(Math.abs(deltaFunding), t.currency)}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={`text-right bt-num ${t.r_multiple == null ? "text-[var(--bt-text-muted)]" : t.r_multiple >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}`}>
                    {t.r_multiple == null ? "—" : `1 : ${t.r_multiple.toFixed(1)}`}
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 text-xs border border-[var(--bt-accent)]/40 text-[var(--bt-accent)] rounded-sm">
                      {t.emotion}
                    </span>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const src = t.source || "Own Analysis";
                      const color = SOURCE_COLORS[src] || "#888";
                      return (
                        <span
                          data-testid={`trade-source-tag-${t.id}`}
                          className="px-2 py-0.5 text-xs border rounded-sm whitespace-nowrap"
                          style={{ borderColor: `${color}66`, color: color, background: `${color}10` }}
                        >
                          {src}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1 items-center">
                      {t.has_screenshot && (
                        <button
                          type="button"
                          data-testid={`screenshot-badge-${t.id}`}
                          onClick={() => openScreenshot(t)}
                          aria-label="View chart screenshot"
                          title="View chart screenshot"
                          className="p-2 border rounded-sm transition-all duration-150 hover:scale-105"
                          style={{
                            borderColor: "rgba(0, 255, 136, 0.3)",
                            color: "#00ff88",
                            background: "rgba(0, 255, 136, 0.06)",
                          }}
                        >
                          <Camera className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <Link to={`/trades/${t.id}/edit`} data-testid={`edit-trade-${t.id}`}
                        className="p-2 border border-[var(--bt-border)] hover:border-[var(--bt-accent)] rounded-sm transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>
                      {/* iter126 — expand toggle. Reveals MFE/MAE card
                          in a full-width sub-row below. */}
                      <button
                        type="button"
                        data-testid={`toggle-detail-${t.id}`}
                        aria-label={expanded.has(t.id) ? "Hide trade detail" : "Show trade detail"}
                        aria-expanded={expanded.has(t.id)}
                        onClick={() => toggleExpanded(t.id)}
                        className="p-2 border border-[var(--bt-border)] hover:border-[var(--bt-accent)] rounded-sm transition-colors"
                      >
                        {expanded.has(t.id)
                          ? <ChevronUp className="w-3.5 h-3.5" />
                          : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-trade-${t.id}`}
                            className="p-2 border border-[var(--bt-border)] hover:border-[var(--bt-danger)] hover:text-[var(--bt-danger)] rounded-sm transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[var(--bt-surface)] border-[var(--bt-border)]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Delete this trade?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t.symbol} · {t.date} · {formatMoney(t.pnl, t.currency)} — this cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[var(--bt-border)] hover:bg-[var(--bt-surface-hover)]">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`confirm-delete-${t.id}`}
                              onClick={() => onDelete(t.id)}
                              className="bg-[var(--bt-danger)] hover:bg-[var(--bt-danger-hover)] text-white">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
                {/* iter126 — desktop expanded detail row. Full-width
                    sub-row below the trade; only rendered when the
                    chevron is toggled open. Fragment wrapper allows
                    two sibling <TableRow>s from a single map iteration. */}
                {expanded.has(t.id) && (
                  <TableRow key={`${t.id}-detail`} data-testid={`trade-detail-row-${t.id}`}
                            className="border-[var(--bt-border)] hover:bg-transparent">
                    <TableCell colSpan={13} className="py-3 px-4 bg-[color-mix(in_srgb,var(--bt-accent)_3%,var(--bt-surface))]">
                      {t.data_source === "delta_api" && (t.trading_fees ?? 0) > 0 && (
                        <p
                          data-testid={`fee-truncation-note-${t.id}`}
                          className="text-[11px] text-[var(--bt-text-muted)] mb-3"
                        >
                          Fees shown rounded to 2 decimals. Exact commission Delta deducted:{" "}
                          <span className="font-mono text-[var(--bt-text)]">
                            {formatMoneyExact(t.trading_fees_exact ?? t.trading_fees, t.currency)}
                          </span>
                          . Delta's Fills tab truncates fees, so it may differ by $0.01.
                        </p>
                      )}
                      <MfeMaeCard tradeId={t.id} testid={`mfe-mae-desktop-${t.id}`} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div data-testid="trades-pagination" className="flex items-center justify-between text-sm">
          <p className="text-[var(--bt-text-muted)]">
            Page <span className="text-white font-semibold">{page + 1}</span> of {totalPages} ·{" "}
            <span className="font-mono">{total}</span> trades
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => goToPage(Math.max(0, page - 1))}
              disabled={page === 0 || loading}
              variant="outline" data-testid="pagination-prev-btn"
              className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm h-9">
              ← Prev
            </Button>
            <Button
              onClick={() => goToPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1 || loading}
              variant="outline" data-testid="pagination-next-btn"
              className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm h-9">
              Next →
            </Button>
          </div>
        </div>
      )}

      <ScreenshotLightbox
        open={lightbox.open}
        src={lightbox.src}
        caption={lightbox.caption}
        onClose={closeLightbox}
      />
    </div>
  );
}
